import { useMemo, useState } from "react";
import { Search, ShieldCheck, Usb, Cable } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { useWorkspace } from "../../store/WorkspaceContext";

export const SupportedPlottersDialog = () => {
  const { state, dispatch } = useWorkspace();
  const open = state.dialog === "plotters";
  const [q, setQ] = useState("");
  const machines = state.machines;
  const groups = useMemo(() => {
    const t = q.trim().toLowerCase();
    const list = (machines || []).filter((m) => !t || `${m.brand} ${m.model} ${m.name} ${m.notes || ""} ${m.command_format}`.toLowerCase().includes(t));
    const by = new Map();
    list.forEach((m) => { if (!by.has(m.brand)) by.set(m.brand, []); by.get(m.brand).push(m); });
    return [...by.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [machines, q]);
  const close = () => dispatch({ type: "SET", patch: { dialog: null } });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent className="panel3d max-h-[88vh] max-w-3xl overflow-y-auto border-[#27324a] bg-[#121824] text-[#e8e9ec]" data-testid="plotters-dialog">
        <DialogHeader>
          <DialogTitle className="text-[15px]">Supported plotters <span className="ml-1 font-mono text-[12px] text-[#9aa0a8]" data-testid="plotters-count">{machines?.length || 0} models</span></DialogTitle>
          <DialogDescription className="text-[#9aa0a8]">Every cutter below is auto-detected when it's plugged into the shop computer, with its own HP-GL / DMPL setup, cut width, speed, force and blade offset. Not listed? Most vinyl cutters that install as a Windows printer or a COM port work with the generic profiles.</DialogDescription>
        </DialogHeader>
        <label className="relative block"><Search className="pointer-events-none absolute left-2.5 top-2 h-4 w-4 text-[#6b7079]" />
          <input data-testid="plotters-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search brand or model… e.g. Graphtec, MH, Summa" className="field pl-8" /></label>
        <div className="space-y-3" data-testid="plotters-list">
          {groups.map(([brand, list]) => (
            <div key={brand} className="rounded-md border border-[#27324a] bg-[#0c1017]" data-testid={`plotters-brand-${brand.replace(/\s+/g, "-").toLowerCase()}`}>
              <div className="border-b border-[#27324a] px-3 py-1.5 text-[12px] font-semibold text-[#e8e9ec]">{brand} <span className="ml-1 font-normal text-[#6b7079]">{list.length}</span></div>
              <ul className="divide-y divide-[#1a2233]">
                {list.map((m) => (
                  <li key={m.id} className="grid grid-cols-[1fr_auto] gap-x-3 px-3 py-2 text-[12px]" data-testid={`plotter-${m.id}`}>
                    <div>
                      <div className="flex items-center gap-1.5 font-medium">{m.name}{m.certified && <ShieldCheck className="h-3.5 w-3.5 text-[#3ecf8e]" title="Certified profile" />}</div>
                      {m.notes && <div className="mt-0.5 text-[11px] text-[#9aa0a8]">{m.notes}</div>}
                    </div>
                    <div className="text-right font-mono text-[11px] text-[#9aa0a8]">
                      <div className="flex items-center justify-end gap-1">{/usb/i.test(m.transport || "") ? <Usb className="h-3 w-3" /> : <Cable className="h-3 w-3" />}{m.transport || "USB/Serial"}</div>
                      <div>{m.command_format.toUpperCase()} · max {m.max_cut_width_in}" wide</div>
                    </div>
                  </li>))}
              </ul>
            </div>))}
          {!groups.length && <div className="py-6 text-center text-[12px] text-[#9aa0a8]" data-testid="plotters-empty">No plotter matches “{q}”. Generic HP-GL / DMPL profiles cover most other vinyl cutters.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
